import { Record } from "../record";
import { InterfaceDefinition, InterfaceRef } from "./interfaces";
import { FieldTypesFor } from "./types";


export interface ClassDefinition<T = any> extends InterfaceDefinition<T>, Constructor<T>
{
    extensions?: Record<string, ClassRef>;
}


export type ClassRef<T = any> = InterfaceRef<T> & Constructor<T>;
{
}

export namespace ClassRef
{
    export function define<T>(classRef: Constructor<T>, name: string, fields?: FieldTypesFor<T>): ClassDefinition<T>
    {
        // Validate fields
        if (fields)
        {
            const invalidEntries =
                Record.entries(fields)
                    .filter(([name, type]) => !type);
            if (invalidEntries.length > 0)
            {
                const fieldNames = invalidEntries.map(([field]) => `${name}.${field}`).join(", ");
                throw new Error(`Missing type for field(s): ${fieldNames}.`);
            }
        }

        // Resolve base
        const base: ClassRef =
            (classRef.__proto__ != Object.__proto__)
                ? classRef.__proto__
                : Object;

        // Define reflection members
        Object.defineProperty(classRef, "name", { value: name, writable: false });
        Object.defineProperty(classRef, "fields", { value: fields, writable: false });
        Object.defineProperty(classRef, "base", { value: base, writable: false });

        // Extensions
        if (base != Object)
        {
            if (!base.extensions)
                Object.defineProperty(base, "extensions", { value: {}, writable: false });
            base.extensions![name] = classRef as ClassRef;
        }

        return classRef as ClassDefinition<T>;
    }

    export function isClassRef(value: any): value is ClassRef;
    export function isClassRef(value: ClassRef): value is ClassRef
    {
        if (typeof value != "function")
            return false;

        if (!value.name || !value.fields)
            return false;

        return true;
    }
}


export interface Constructor<T> extends Function
{
    new(...args: any): T;
}


declare global
{
    interface Object
    {
        __proto__: any;
    }
}

